import { Context } from '../context/Context';
import { BoundingDimensions } from '../util';
import { Mesh, MeshLayout } from './Mesh';
import { VertexBuffer } from './VertexBuffer';
import { RenderObject } from '../object/RenderObject';
import { Scene } from '../scene/Scene';

/**
 * A cross-billboard mesh: 3 quads intersecting at 60° intervals (star pattern
 * viewed from above).  Used as a cheap LOD impostor for vegetation at distance.
 *
 * Each quad has two triangles, so 6 triangles / 18 vertices total (non-indexed
 * for simplicity — at 6 triangles the overhead is negligible).
 *
 * Vertex layout: position (3) + normal (3) + uv (2) = 8 floats per vertex.
 */
export class CrossBillboardMesh extends Mesh {
  private get bounds(): BoundingDimensions {
    return this.localBounds;
  }

  /**
   * Create a cross-billboard mesh sized to fit the given dimensions.
   *
   * @param width   Width of each quad (typically the tree's bounding box width)
   * @param height  Height of each quad (typically the tree's bounding box height)
   * @param offsetY Vertical offset to center the billboard on the tree trunk base
   */
  public static create(
    name: string,
    ctx: Context,
    scene: Scene,
    width: number,
    height: number,
    offsetY = 0,
  ): CrossBillboardMesh {
    const mesh = new CrossBillboardMesh(name, ctx, scene);
    mesh.buildGeometry(width, height, offsetY);
    return mesh;
  }

  private buildGeometry(width: number, height: number, offsetY: number): void {
    const gl = this.ctx.gl;
    const hw = width / 2;

    // 3 quads at 0°, 60°, 120° around Y axis
    const angles = [0, Math.PI / 3, (2 * Math.PI) / 3];
    const vertices: number[] = [];

    for (const angle of angles) {
      const nx = Math.sin(angle); // normal X
      const nz = Math.cos(angle); // normal Z
      const dx = nz * hw; // perpendicular to normal = along the quad face
      const dz = -nx * hw;

      // Quad corners: bottom-left, bottom-right, top-left, top-right
      const bl = [-dx, offsetY, -dz];
      const br = [dx, offsetY, dz];
      const tl = [-dx, offsetY + height, -dz];
      const tr = [dx, offsetY + height, dz];

      // Triangle 1: bl, br, tl  (V=1 at bottom, V=0 at top — standard UV)
      pushVertex(vertices, bl, nx, 0, nz, 0, 1);
      pushVertex(vertices, br, nx, 0, nz, 1, 1);
      pushVertex(vertices, tl, nx, 0, nz, 0, 0);

      // Triangle 2: br, tr, tl
      pushVertex(vertices, br, nx, 0, nz, 1, 1);
      pushVertex(vertices, tr, nx, 0, nz, 1, 0);
      pushVertex(vertices, tl, nx, 0, nz, 0, 0);
    }

    const data = new Float32Array(vertices);
    const vertexCount = data.length / 8;
    const stride = 8; // floats per vertex

    // Set data layout
    this.dataLayout = {
      position: { offset: 0, length: 3 },
      normal: { offset: 3, length: 3 },
      diffuseTexture: { offset: 6, length: 2 },
    } as MeshLayout;

    // Compute bounds
    this._localBounds = BoundingDimensions.createFromData(data, vertexCount, 0, stride);

    // Upload to GPU
    const glBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, glBuf);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);

    this.buffer = new VertexBuffer(glBuf, gl.TRIANGLES, stride, vertexCount);

    this.createVAO(gl);
  }

  public createInstance(): RenderObject {
    const obj = new RenderObject(this);
    obj.bounds = this.bounds.clone();
    obj.bounds.bind(obj.trs);
    return obj;
  }
}

function pushVertex(
  out: number[],
  pos: number[],
  nx: number,
  ny: number,
  nz: number,
  u: number,
  v: number,
): void {
  out.push(pos[0], pos[1], pos[2], nx, ny, nz, u, v);
}
